/**
 * Custom hook providing status operations for the knowledge graph chat.
 * Operates on Apollo reactive variables for state management.
 * @remarks
 * Provides immutable operations for managing agent status updates, progress
 * tracking, and status display configuration. All operations create new
 * arrays/objects rather than mutating existing state.
 *
 * Operations include:
 * - Updating current status from SSE stream
 * - Adding agent progress items
 * - Clearing agent progress
 * - Updating status display configuration
 * - Toggling status update visibility
 *
 * All operations are wrapped in useCallback for stable references across renders.
 * @returns Status operation functions for chat state management
 */

import { useCallback } from "react";

import type {
  IStatusUpdate,
  IAgentProgress,
  IStatusDisplayConfig,
} from "@/stores/knowledgeGraph";
import {
  currentStatusVar,
  agentProgressVar,
  showStatusUpdatesVar,
  statusDisplayConfigVar,
} from "@/stores/knowledgeGraph";

/**
 * Hook providing status operations for knowledge graph chat.
 * @returns Object containing status operation functions
 */
export const useStatusOperations = () => {
  /**
   * Update the current status from SSE stream.
   * @param status - Status update or null to clear
   */
  const updateCurrentStatus = useCallback((status: IStatusUpdate | null) => {
    currentStatusVar(status);
  }, []);

  /**
   * Record an agent progress event, keeping one row per step.
   * @param progress - Agent progress to record
   * @remarks
   * SE-8646: v2 emits `start` then `complete` for every graph node under the
   * same `agent`, so appending each event showed every step twice. When the
   * agent's most recent row is still running, the event updates that row in
   * place; otherwise it appends, so a node the agent loop runs again after it
   * completed gets a row of its own.
   */
  const addAgentProgress = useCallback((progress: IAgentProgress) => {
    const currentProgress = agentProgressVar();
    const latestIndex = currentProgress
      .map(row => row.agent)
      .lastIndexOf(progress.agent);
    if (currentProgress[latestIndex]?.status !== "running") {
      agentProgressVar([...currentProgress, progress]);
      return;
    }
    agentProgressVar(
      currentProgress.map((row, index) =>
        index === latestIndex ? { ...row, ...progress } : row
      )
    );
  }, []);

  /**
   * Clear all agent progress items.
   */
  const clearAgentProgress = useCallback(() => {
    agentProgressVar([]);
  }, []);

  /**
   * Update status display configuration.
   * @param config - Partial configuration object with properties to update
   */
  const updateStatusDisplayConfig = useCallback(
    (config: Partial<IStatusDisplayConfig>) => {
      const currentConfig = statusDisplayConfigVar();
      statusDisplayConfigVar({ ...currentConfig, ...config });
    },
    []
  );

  /**
   * Toggle status update visibility.
   */
  const toggleShowStatusUpdates = useCallback(() => {
    const currentValue = showStatusUpdatesVar();
    showStatusUpdatesVar(!currentValue);
  }, []);

  return {
    updateCurrentStatus,
    addAgentProgress,
    clearAgentProgress,
    updateStatusDisplayConfig,
    toggleShowStatusUpdates,
  };
};
